import React, { Fragment, useState } from "react";
import { Dialog, Disclosure, Popover, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useStaticQuery, Link, graphql } from "gatsby";
import AssemblyLogo from "../Logo";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const NavLink = ({ className, url, label, rel, hoverText }) => {
  if (url && url.startsWith("http")) {
    return (
      <a
        href={url}
        title={hoverText}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        {label}
      </a>
    );
  }

  return (
    <Link to={url} rel={rel} title={hoverText} className={className}>
      {label}
    </Link>
  );
};

export default function Header({ page }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const data = useStaticQuery(graphql`
    {
      allContentfulNavigation {
        nodes {
          name
          id
          items {
            id
            url
            rel
            label
            hoverText
            subnav {
              name
              id
              items {
                label
                id
                hoverText
                rel
                url
              }
            }
          }
        }
      }
    }
  `);

  const { nodes } = data?.allContentfulNavigation;
  const mainNav = nodes.find(
    (nav) => nav.id === "00cd3404-880b-501b-9eb5-9179d50a2edf"
  );

  if (!mainNav) {
    return null;
  }

  const mainNavItems = mainNav?.items || [];
  const navLinks = mainNavItems.map((item) => {
    return {
      ...item,
      hasSubNav: item.subnav && item.subnav.length > 0 ? true : false,
    };
  });

  return (
    <header className={"mx-auto max-w-screen-xl py-6 bg-white"}>
      <nav
        className="max-w-screen-xl relative flex container items-center justify-between px-6"
        aria-label="Global"
      >
        <Link to="/">
          <span className="sr-only">Assembly Inc.</span>
          <AssemblyLogo fill={"#000000"} />
        </Link>
        <div className="flex lg:hidden">
          <button
            type="button"
            className={
              "inline-flex items-center justify-center rounded-md p-2.5 text-white"
            }
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6 text-gray-700" aria-hidden="true" />
          </button>
        </div>
        <Popover.Group className="hidden lg:flex lg:gap-x-12 items-center">
          {navLinks.map((link) => {
            if (link.hasSubNav) {
              return (
                <Popover key={link.id} className="relative">
                  <Popover.Button
                    className={
                      "flex items-center text-sm text-gray-900 font-medium"
                    }
                  >
                    {link.label}
                    <ChevronDownIcon
                      className="h-5 w-5 flex-none text-gray-400"
                      aria-hidden="true"
                    />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-lg overflow-hidden rounded-2xl shadow-lg ring-1 ring-gray-900/5">
                      {link.subnav.map((subnav, i) => (
                        <div
                          key={subnav.id}
                          className={
                            i === 0 || i % 2 === 0
                              ? classNames("p-10", "bg-gray-100")
                              : classNames("p-10", "bg-white")
                          }
                        >
                          <div className="grid grid-cols-2 gap-4">
                            <div className="text-sm font-semibold py-2">
                              {subnav.name}
                            </div>
                            <ul role="navigation">
                              {subnav.items.map((item) => (
                                <li key={item.id} className="py-2">
                                  <NavLink
                                    {...item}
                                    className="text-sm font-medium"
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </Popover.Panel>
                  </Transition>
                </Popover>
              );
            } else if (link.url === "/contact-us") {
              return (
                <NavLink
                  {...link}
                  key={link.id}
                  className={
                    "rounded-full bg-black text-white text-sm px-4 py-2 hover:text-black hover:bg-green font-medium"
                  }
                />
              );
            } else {
              return (
                <NavLink
                  {...link}
                  key={link.id}
                  className={"text-sm text-black font-medium"}
                />
              );
            }
          })}
        </Popover.Group>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <div className="flex lg:flex-1 max-w-sm">
              <Link to="/" className="-m-1.5 p-1.5">
                <span className="sr-only">Assembly Inc.</span>
                <AssemblyLogo />
              </Link>
            </div>
            <button
              type="button"
              className="rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            {navLinks.map((link) => {
              if (link.hasSubNav) {
                return (
                  <div
                    key={link.id}
                    className="-my-6 divide-y divide-gray-500/10"
                  >
                    <div className="space-y-2 py-6">
                      <Disclosure defaultOpen as="div" className="-mx-6">
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                              {link.label}
                              <ChevronDownIcon
                                className={classNames(
                                  open ? "rotate-180" : "",
                                  "h-5 w-5 flex-none"
                                )}
                                aria-hidden="true"
                              />
                            </Disclosure.Button>
                            {link.subnav.map((subnav, i) => (
                              <Disclosure.Panel
                                key={subnav.id}
                                className={
                                  i === 0 || i % 2 === 0
                                    ? classNames("p-4", "bg-gray-100")
                                    : classNames("p-4", "bg-white")
                                }
                              >
                                <div className="ml-3 font-semibold py-2">
                                  {subnav.name}
                                </div>
                                <ul role="navigation">
                                  {subnav.items.map((item) => (
                                    <li key={item.id} className="ml-6 py-2">
                                      <NavLink {...item} />
                                    </li>
                                  ))}
                                </ul>
                              </Disclosure.Panel>
                            ))}
                          </>
                        )}
                      </Disclosure>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={link.id} className="-mx-6">
                    <div className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                      <NavLink {...link} />
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
